import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Moon, Sun } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const [navCtaUrl, setNavCtaUrl] = useState('#contact');
  
  // Load navigation CTA URL from localStorage
  useEffect(() => {
    const savedUrl = localStorage.getItem('nav-cta-url') || '#contact';
    setNavCtaUrl(savedUrl);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const isDarkMode = localStorage.getItem("theme") === "dark" || !localStorage.getItem("theme") && window.matchMedia("(prefers-color-scheme: dark)").matches;
    setIsDark(isDarkMode);
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, []);
  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
    document.documentElement.classList.toggle("dark", newTheme);
  };
  // Navigation items as anchor links for single page
  const navItems = [
    { key: 'nav.home', href: '#home' },
    { key: 'nav.services', href: '#services' },
    { key: 'nav.process', href: '#process' },
    { key: 'nav.contact', href: '#contact' }
  ];
  
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const isActive = (path: string) => location.pathname === path;
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-background/95 backdrop-blur-md border-b border-border shadow-sm" : "bg-transparent"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Header Row */}
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo - Left Side */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img 
                src="./images/LOGO-A9.png" 
                alt="Reesha Logo" 
                className="h-8 md:h-10 w-auto"
              />
            </Link>
          </div>
          

          {/* Language Switcher & Actions - Right Side */}
          <div className={`hidden lg:flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            <LanguageSwitcher variant="compact" className="" />
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="w-9 h-9">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button 
              onClick={() => {
                if (navCtaUrl.startsWith('#')) {
                  scrollToSection(navCtaUrl);
                } else if (navCtaUrl.startsWith('mailto:') || navCtaUrl.startsWith('tel:')) {
                  window.location.href = navCtaUrl;
                } else {
                  window.open(navCtaUrl, '_blank');
                }
              }}
              className="btn-glow"
            >
              {t('hero.cta.primary')}
            </Button>
          </div>
        </div>
        
        {/* Navigation Row - Centered and Below */}
        <div className="hidden lg:flex justify-center py-3 border-t border-white/10">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
            {navItems.map(item => 
              <button 
                key={item.href} 
                onClick={() => scrollToSection(item.href)}
                className="text-sm font-medium transition-colors duration-200 hover:text-primary text-muted-foreground"
              >
                {t(item.key)}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Mobile Menu - Outside container for proper positioning */}
      <div className="lg:hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-end py-2 border-t border-white/10">
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="w-9 h-9">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="w-9 h-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col space-y-4 mt-8">
                  {navItems.map(item => 
                    <button 
                      key={item.href} 
                      onClick={() => {
                        scrollToSection(item.href);
                        setIsOpen(false);
                      }}
                      className="text-lg font-medium transition-colors duration-200 hover:text-primary text-muted-foreground text-left"
                    >
                      {t(item.key)}
                    </button>
                  )}
                  <div className="pt-4 border-t border-border">
                    <Button 
                      onClick={() => {
                        if (navCtaUrl.startsWith('#')) {
                          scrollToSection(navCtaUrl);
                        } else if (navCtaUrl.startsWith('mailto:') || navCtaUrl.startsWith('tel:')) {
                          window.location.href = navCtaUrl;
                        } else {
                          window.open(navCtaUrl, '_blank');
                        }
                        setIsOpen(false);
                      }}
                      className="w-full btn-glow"
                    >
                      {t('hero.cta.primary')}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>;
};
export default Navigation;